import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Save, Calendar, Repeat, Trash2, Image as ImageIcon } from 'lucide-react';
import { gymModalInput, gymModalLabel } from '../gymPageTheme';
import WeekdayScheduleEditor from './WeekdayScheduleEditor';
import { validateDaySchedules } from '../../../utils/weekdaySchedule';

const COLORS = [
    { name: 'Blu', value: '#3B82F6' },
    { name: 'Verde', value: '#10B981' },
    { name: 'Rosso', value: '#EF4444' },
    { name: 'Giallo', value: '#F59E0B' },
    { name: 'Viola', value: '#8B5CF6' },
    { name: 'Rosa', value: '#EC4899' },
    { name: 'Indaco', value: '#6366F1' },
    { name: 'Teal', value: '#14B8A6' }
];

const CATEGORIES = [
    'Yoga',
    'Pilates',
    'CrossFit',
    'Spinning',
    'Zumba',
    'Aerobica',
    'Functional',
    'Boxe',
    'Kick Boxing',
    'Calisthenics',
    'Altro'
];

export default function CreateCourseModal({ isOpen, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        max_participants: '',
        is_unlimited: false,
        is_recurring: true, // Sempre true
        recurrence_type: 'weekly',
        day_schedules: [],
        recurrence_end_date: '',
        color: '#3B82F6'
    });
    
    // Stati per logo upload
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Reset error quando l'utente modifica
        if (error) setError(null);
    };

    const handleLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Validazione tipo file
        const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Tipo file non supportato. Usa PNG, JPG o WEBP');
            return;
        }
        
        // Validazione dimensione (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('File troppo grande. Massimo 2MB');
            return;
        }
        
        setLogoFile(file);
        
        // Crea preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };
    
    const handleUploadLogo = async (courseId) => {
        if (!logoFile || !courseId) return;
        
        try {
            setUploadingLogo(true);
            const api = getAuthenticatedApi();
            
            const formData = new FormData();
            formData.append('logo', logoFile);
            
            const response = await api.post(
                `/course_logo_upload.php?course_id=${courseId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            if (response.data.success) {
                const timestamp = response.data.timestamp || Date.now();
                setLogoPreview(`/api/serve_image.php?filename=${response.data.filename}&type=logo&v=${timestamp}`);
                setLogoFile(null);
                toast.success('Logo caricato con successo!');
            }
        } catch (err) {
            console.error('Errore upload logo corso:', err);
            toast.error(err.response?.data?.error || 'Errore nel caricamento del logo');
        } finally {
            setUploadingLogo(false);
        }
    };
    
    const handleDeleteLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
        toast.success('Preview logo rimossa');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validazione
        if (!formData.title.trim()) {
            setError(t('gym.courses.createModal.titleRequired'));
            return;
        }
        
        // Validazione lezioni programmate (sempre obbligatorie)
        const scheduleError = validateDaySchedules(formData.day_schedules);
        if (scheduleError === 'empty') {
            setError(t('gym.courses.createModal.selectDaysRequired'));
            return;
        }
        if (scheduleError === 'time') {
            setError(t('gym.courses.weekdaySchedule.invalidTime'));
            return;
        }
        if (!formData.recurrence_end_date) {
            setError(t('gym.courses.createModal.endDateRequired'));
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const api = getAuthenticatedApi();
            
            // Prepara dati per l'API
            const courseData = {
                title: formData.title.trim(),
                description: formData.description.trim() || null,
                category: formData.category || null,
                is_unlimited: formData.is_unlimited,
                max_participants: formData.is_unlimited ? null : (parseInt(formData.max_participants) || null),
                is_recurring: formData.is_recurring,
                color: formData.color
            };
            
            // Aggiungi sempre i dati delle lezioni programmate (corso ricorrente)
            courseData.recurrence_type = formData.recurrence_type;
            courseData.day_schedules = formData.day_schedules;
            courseData.recurrence_days = formData.day_schedules.map((slot) => slot.day);
            courseData.recurrence_end_date = formData.recurrence_end_date;
            courseData.standard_start_time = `${formData.day_schedules[0].start_time}:00`;
            courseData.standard_end_time = `${formData.day_schedules[0].end_time}:00`;
            
            const response = await api.post('/gym_courses.php?action=create_course', courseData);
            
            if (response.data.success) {
                const courseId = response.data.course_id;
                
                // Carica logo se presente
                if (logoFile && courseId) {
                    await handleUploadLogo(courseId);
                }
                
                // Mostra messaggio di successo
                const sessionsCount = response.data.sessions_generated?.sessions_created || 0;
                if (sessionsCount > 0) {
                    toast.success(t('gym.courses.createModal.success', { count: sessionsCount }));
                }
                onSuccess();
            } else {
                throw new Error(response.data.error || t('gym.courses.errors.createError'));
            }
            
        } catch (err) {
            console.error('Errore creazione corso:', err);
            setError(err.response?.data?.error || err.message || t('gym.courses.errors.createError'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="flex h-full min-h-0 flex-col">
                {error && (
                    <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 overflow-y-auto p-5 lg:grid-cols-[minmax(320px,0.85fr)_minmax(0,1.15fr)] lg:overflow-hidden lg:p-6">
                    <section className="space-y-4 lg:overflow-y-auto lg:pr-1">
                        <h4 className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                            <Calendar size={18} />
                            {t('gym.courses.createModal.basicInfo')}
                        </h4>

                        <div>
                            <label className={gymModalLabel}>
                                {t('gym.courses.createModal.titleLabel')} *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className={gymModalInput}
                                placeholder={t('gym.courses.createModal.titlePlaceholder')}
                                required
                            />
                        </div>

                        <div>
                            <label className={gymModalLabel}>
                                {t('gym.courses.createModal.descriptionLabel')}
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={2}
                                className={gymModalInput}
                                placeholder={t('gym.courses.createModal.descriptionPlaceholder')}
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className={gymModalLabel}>
                                    {t('gym.courses.createModal.categoryLabel')}
                                </label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    className={gymModalInput}
                                >
                                    <option value="">{t('gym.courses.createModal.categoryPlaceholder')}</option>
                                    {CATEGORIES.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className={gymModalLabel}>
                                    {t('gym.courses.createModal.colorLabel')}
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {COLORS.map(color => (
                                        <button
                                            key={color.value}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                                            className={`h-8 w-8 rounded-full border-2 transition-transform ${
                                                formData.color === color.value
                                                    ? 'scale-110 border-gray-900 dark:border-white'
                                                    : 'border-transparent hover:scale-110'
                                            }`}
                                            style={{ backgroundColor: color.value }}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleLogoFileChange}
                                className="hidden"
                                id="create-course-logo-input"
                            />
                            {logoPreview ? (
                                <img
                                    src={logoPreview}
                                    alt=""
                                    className="h-12 w-12 rounded-lg border border-gray-300 object-cover dark:border-white/15"
                                />
                            ) : null}
                            <label
                                htmlFor="create-course-logo-input"
                                className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-white/90"
                            >
                                <ImageIcon size={16} />
                                {logoPreview ? 'Cambia logo' : 'Logo'}
                            </label>
                            {logoPreview ? (
                                <button
                                    type="button"
                                    onClick={handleDeleteLogo}
                                    className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400"
                                >
                                    <Trash2 size={14} />
                                    Rimuovi
                                </button>
                            ) : (
                                <span className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG, WEBP · max 2MB</span>
                            )}
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className={gymModalLabel}>
                                    {t('gym.courses.createModal.maxParticipantsLabel')}
                                </label>
                                {formData.is_unlimited ? (
                                    <p className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                                        {t('gym.courses.createModal.unlimitedParticipants')}
                                    </p>
                                ) : (
                                    <input
                                        type="number"
                                        name="max_participants"
                                        value={formData.max_participants}
                                        onChange={handleChange}
                                        min="1"
                                        className={gymModalInput}
                                        placeholder={t('gym.courses.createModal.maxParticipantsPlaceholder')}
                                    />
                                )}
                                <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-white/85">
                                    <input
                                        type="checkbox"
                                        id="is_unlimited"
                                        name="is_unlimited"
                                        checked={formData.is_unlimited}
                                        onChange={handleChange}
                                        className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
                                    />
                                    {t('gym.courses.createModal.unlimitedParticipants')}
                                </label>
                            </div>
                            <div>
                                <label className={gymModalLabel}>
                                    {t('gym.courses.createModal.endDateLabel')} *
                                </label>
                                <input
                                    type="date"
                                    name="recurrence_end_date"
                                    value={formData.recurrence_end_date}
                                    onChange={handleChange}
                                    min={new Date().toISOString().split('T')[0]}
                                    className={gymModalInput}
                                    required={formData.is_recurring}
                                />
                            </div>
                        </div>
                    </section>

                    <section className="min-h-0 space-y-3 lg:overflow-y-auto lg:pr-1">
                        <h4 className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                            <Repeat size={18} />
                            {t('gym.courses.createModal.recurrenceTitle')}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('gym.courses.createModal.recurringCourse')}
                        </p>
                        <WeekdayScheduleEditor
                            compact
                            slots={formData.day_schedules}
                            onChange={(day_schedules) => {
                                setFormData((prev) => ({ ...prev, day_schedules }));
                                if (error) setError(null);
                            }}
                        />
                    </section>
                    </div>

                    <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 px-5 py-3 dark:border-white/10 lg:px-6">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100 dark:text-white/85 dark:hover:bg-gray-700"
                        >
                            {t('gym.courses.createModal.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-2 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {loading ? (
                                <>
                                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                    <span>{t('gym.courses.createModal.creating')}</span>
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    <span>{t('gym.courses.createModal.create')}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
        </div>
    );
}

